import type {
  ComponentNode,
  CustomDisciplineButtonComponentProps,
} from "../layout/types.js";
import { applyPatchToCustomDisciplineButtonNode } from "./applyRuntimePatches.js";
import { collectBehaviorDependencies } from "./compileEvents.js";
import { compileBehavior } from "./compileBehavior.js";
import { evaluateWithRenderCache } from "./renderCache.js";
import {
  applyRuntimeDomTargetFlags,
  runCompiledNodeBehavior,
  withCurrentNodeGeometry,
} from "./applyNodeBehavior.js";
import type {
  CompiledCustomDisciplineButtonNode,
  CompileNodeContext,
  RenderCustomDisciplineButtonNode,
  RuntimeContext,
} from "./types.js";
import { compileNodeStyle, transitionsTouchTopLeft } from "./helpers.js";
import { uniqueDependencies } from "./method.js";

/**
 * Compiles a `customDisciplineButton` component node (custom discipline cell/modal
 * layouts only). The button itself carries no data binding; clicking it dispatches
 * a node-targeted `onClick` behavior evaluation (see `runBehaviorFlows.ts`), whose
 * `set` actions (`customListSetField`, `customOpenModal`, …) surface as one-shot
 * `customDiscipline.action` runtime effects for the host page to apply.
 */
export function compileCustomDisciplineButtonNode(
  node: ComponentNode,
  compileContext: CompileNodeContext,
): CompiledCustomDisciplineButtonNode {
  if (
    node.kind !== "component" ||
    node.component !== "customDisciplineButton"
  ) {
    throw new Error("Node is not a customDisciplineButton component");
  }

  const baseStyle = compileNodeStyle(node.style, node.transitions);
  const props = (node.props ?? {}) as CustomDisciplineButtonComponentProps;
  const behavior = node.behavior
    ? compileBehavior(
        node.behavior,
        node.id,
        compileContext.catalogDeps,
        undefined,
        compileContext.layoutNodeById,
      )
    : undefined;

  const dependencies = uniqueDependencies(
    behavior ? collectBehaviorDependencies(behavior, compileContext) : [],
  );

  return {
    id: node.id,
    dependencies,

    evaluate(ctx: RuntimeContext): RenderCustomDisciplineButtonNode | null {
      return evaluateWithRenderCache(ctx, node.id, dependencies, () => {
        let renderNode: RenderCustomDisciplineButtonNode = {
          id: node.id,
          key: ctx.__nodeInstanceKeyPrefix
            ? `${ctx.__nodeInstanceKeyPrefix}${node.id}`
            : node.id,
          x: node.x,
          y: node.y,
          width: node.width,
          height: node.height,
          zIndex: node.zIndex,
          rotation: node.rotation ?? 0,
          kind: "customDisciplineButton",
          props: {
            label: props.label,
            autoFocus: props.autoFocus,
            startIcon: props.startIcon,
            variant: props.variant,
            size: props.size,
            color: props.color,
            disabled: props.disabled,
          },
          style: { ...baseStyle },
          hasTopLeftTransition: transitionsTouchTopLeft(node.transitions),
        };

        if (behavior) {
          const behaviorCtx = withCurrentNodeGeometry(ctx, {
            x: node.x,
            y: node.y,
            width: node.width,
            height: node.height,
            rotation: node.rotation ?? 0,
          });
          const { patches, effects, continuations, runtimeDomTarget } =
            runCompiledNodeBehavior(behavior, behaviorCtx, node.id);

          for (const patch of patches) {
            renderNode = applyPatchToCustomDisciplineButtonNode(
              renderNode,
              patch,
            );
          }

          ctx.__runtimeEffects?.push(...effects);
          ctx.__runtimeContinuations?.push(...continuations);
          renderNode = applyRuntimeDomTargetFlags(
            renderNode,
            runtimeDomTarget,
            behavior,
          );
        }

        if (renderNode.style.visibility === false) {
          return null;
        }

        return renderNode;
      });
    },
  };
}
